import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useStoreState } from 'easy-peasy'
import PageBuilderSelector from '@v4/utils/selectors/PageBuilderSelector'
import PageBuilderModel from '@v4/store/builderStore/models/PageBuilder.model'
import AppStore from '@v4/store/appStore'
import PageData from '@v4/utils/data/PageData'
import PageTemplateData from '@v4/utils/data/PageTemplateData'
import ScorecardSelector from '@v4/utils/selectors/ScorecardSelector'

export const useSectionList = ({ collection, sectionsMap }) => {
	const isBuilderMode = AppStore.useStoreState(state => state.general.isBuilderMode)
	const sectionIds = PageBuilderSelector.useVisibleSectionGroupIds(collection)
	const pageType = PageBuilderSelector.useType()
	const isPdf = PageData.isTypeResultPdf(pageType)
	const isCollectionEmpty = PageBuilderSelector.useIsCollectionEmpty(collection)
	const isSectionsLocked = ScorecardSelector.useIsSectionsLocked()
	const parent = import.meta.env.SSR ? null : document.getElementById('root')
	const parentRef = useRef(parent)

	parentRef.current = parent

	const focusedField = PageBuilderSelector.useFocusedFieldByScrolling(PageBuilderModel.SCROLLING_TYPE.PREVIEW)
	const activeSectionId = PageBuilderSelector.useActiveSectionId()
	const sectionKey = PageBuilderSelector.useSectionKey(activeSectionId)
	const sortingSectionId = useStoreState(state => state.builder.preview.sortingSectionId)
	const { setSidebarMode } = PageBuilderSelector.useActions()
	const handleSortScroll = useMemo(
		() =>
			_.debounce(sectionId => {
				const el = document.getElementById(`section-${sectionId}`)
				if (el) {
					el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' })
				}
			}, 100),
		[],
	)

	const handleFinish = useCallback(() => {
		if (sortingSectionId) {
			handleSortScroll(sortingSectionId)
		}
	}, [handleSortScroll, sortingSectionId])

	const handleAdd = useCallback(() => {
		setSidebarMode(PageBuilderModel.SIDEBAR_MODE.ADDITION)
	}, [setSidebarMode])

	useEffect(() => {
		if (!focusedField || focusedField.name || focusedField.categoryId || _.startsWith(sectionKey, 'popup/')) return
		handleSortScroll(focusedField.sectionId)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [focusedField])

	return [
		{
			collection,
			sectionIds,
			sectionsMap,
			duration: isBuilderMode ? 300 : 0,
			activeSectionId,
			withAddition: isBuilderMode && isPdf && !isSectionsLocked && !_.isEmpty(sectionIds),
			isMainSectionEmpty: PageTemplateData.isSortGroupMain(collection) && isCollectionEmpty && isBuilderMode,
			pageType,
			parentRef,
		},
		{ handleFinish, handleAdd },
	]
}
